<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'reka-ui';
import { Check, ChevronsUpDown, Plus, Settings } from 'lucide-vue-next';
import NewOrgDialog from '../workspace/NewOrgDialog.vue';
import { useWorkspaceStore } from '../../app/stores/workspace.ts';
import { useInboxStore } from '../../app/stores/inbox.ts';
import { unreadLabel } from './navigation.ts';

defineEmits<{ navigate: [] }>();
const workspace = useWorkspaceStore();
const inbox = useInboxStore();
const router = useRouter();
const creating = ref(false);
const initial = computed(() => (workspace.current?.name ?? '?').trim().charAt(0).toUpperCase());

async function go(slug: string) {
  await router.push({ name: 'org', params: { org: slug } });
}
</script>

<template>
  <DropdownMenuRoot>
    <DropdownMenuTrigger
      class="org-switcher"
      :aria-label="`Organization: ${workspace.current?.name ?? ''}${
        inbox.elsewhere(workspace.currentSlug) ? ' (unread in other organizations)' : ''
      }`"
    >
      <span class="org-mark" aria-hidden="true"
        >{{ initial }}<span v-if="inbox.elsewhere(workspace.currentSlug)" class="nav-icon__dot"
      /></span>
      <span class="sidebar__label truncate spacer">{{ workspace.current?.name ?? 'Dometry' }}</span>
      <ChevronsUpDown
        class="sidebar__label faint"
        :size="14"
        :stroke-width="1.75"
        aria-hidden="true"
      />
    </DropdownMenuTrigger>
    <DropdownMenuPortal>
      <DropdownMenuContent
        class="menu"
        side="bottom"
        align="start"
        :side-offset="4"
        :collision-padding="8"
      >
        <DropdownMenuLabel class="menu__label muted">Organizations</DropdownMenuLabel>
        <DropdownMenuItem
          v-for="org in workspace.orgs"
          :key="org.slug"
          class="menu__item"
          :class="{ 'menu__item--paused': org.paused }"
          @select="go(org.slug).then(() => $emit('navigate'))"
        >
          <span class="spacer truncate">{{ org.name }}</span>
          <span v-if="org.paused" class="badge">Paused</span>
          <span
            v-if="inbox.countFor(org.slug)"
            class="count-badge"
            :aria-label="unreadLabel(inbox.countFor(org.slug))"
            >{{ Math.min(inbox.countFor(org.slug), 99) }}</span
          >
          <Check
            v-if="org.slug === workspace.currentSlug"
            :size="14"
            :stroke-width="2"
            aria-label="Current"
          />
        </DropdownMenuItem>
        <DropdownMenuSeparator class="menu__separator" />
        <DropdownMenuItem
          v-if="workspace.current"
          class="menu__item"
          @select="
            router
              .push({ name: 'org-settings', params: { org: workspace.current.slug } })
              .then(() => $emit('navigate'))
          "
        >
          <Settings :size="15" :stroke-width="1.75" aria-hidden="true" />
          Organization settings
        </DropdownMenuItem>
        <DropdownMenuItem class="menu__item" @select="creating = true">
          <Plus :size="15" :stroke-width="1.75" aria-hidden="true" />
          New organization
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
  <NewOrgDialog v-model:open="creating" />
</template>
